/*
 * Stage 4 (the microscope) geometry: pure functions, no React, no DOM.
 *
 * Unlike the Tagging game's fractions (annotationGeometry.js), a slide's marks
 * are in the IMAGE'S OWN PIXELS, because a length is pixels times the slide's
 * um_per_px. The server measures again and keeps its own number
 * (mushroom-bank/lambda/routes/micro_annotations.py); the limits below mirror
 * that file, and its test reads them from here.
 *
 * A "mark" is one annotation in the making: {feature, shapes, annotation_id?}.
 * A spore is a point, or a length line with an optional width line; a hypha is
 * a polyline.
 */
import { distance } from './annotationGeometry';

export const FEATURES = ['spore', 'hypha', 'clamp_connection', 'basidium', 'cystidium', 'other', 'septum'];

export const MICRO_LIMITS = Object.freeze({
  maxShapes: 200,
  maxPoints: 20000,
  maxPolylinePoints: 2000,
});

const round2 = v => Math.round(v * 100) / 100;

/** Pixels along a path of [x, y] points. */
export function pathLengthPx(points) {
  let total = 0;
  for (let i = 1; i < (points || []).length; i += 1) total += distance(points[i - 1], points[i]);
  return total;
}

/** Micrometres along a path, or null when the slide has no scale. */
export function lengthUm(points, umPerPx) {
  if (!umPerPx) return null;
  return pathLengthPx(points) * Number(umPerPx);
}

/** "5.86 µm", or null. LTR digits; the caller wraps it in dir="ltr". */
export function formatUm(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return `${Number(value).toFixed(2)} µm`;
}

/** The longest line or polyline of a mark, in micrometres (what the server calls measured_um). */
export function measureMark(mark, umPerPx) {
  const lengths = (mark?.shapes || [])
    .filter(s => s.kind === 'line' || s.kind === 'polyline')
    .map(s => lengthUm(s.points, umPerPx))
    .filter(v => v !== null);
  return lengths.length ? Math.max(...lengths) : null;
}

/** A pointer position over the displayed image, as image pixels (clamped, two decimals). */
export function toImagePx(clientX, clientY, rect, width, height) {
  const w = rect.width || 1;
  const h = rect.height || 1;
  const x = Math.min(width, Math.max(0, ((clientX - rect.left) / w) * width));
  const y = Math.min(height, Math.max(0, ((clientY - rect.top) / h) * height));
  return [round2(x), round2(y)];
}

/** Marks with one more shape. A width line joins the newest spore that has a length and no width. */
export function addShape(marks, feature, shape) {
  const list = marks || [];
  if (shape.kind === 'line' && shape.role === 'width') {
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const m = list[i];
      const roles = m.shapes.map(s => s.role);
      if (m.feature === 'spore' && roles.includes('length') && !roles.includes('width')) {
        return list.map((mark, j) => (j === i ? { ...mark, shapes: [...mark.shapes, shape] } : mark));
      }
    }
  }
  return [...list, { feature, shapes: [shape] }];
}

/** Marks without their newest shape (a mark left empty goes too). */
export function undoLast(marks) {
  const list = marks || [];
  if (!list.length) return list;
  const last = list[list.length - 1];
  if (last.shapes.length > 1) {
    return [...list.slice(0, -1), { ...last, shapes: last.shapes.slice(0, -1) }];
  }
  return list.slice(0, -1);
}

/** The first problem the server would refuse, or null. */
export function validateMarks(marks) {
  if (!marks || !marks.length) return 'nothingToSubmit';
  for (const mark of marks) {
    if (!FEATURES.includes(mark.feature)) return 'invalidFeature';
    if (!mark.shapes.length || mark.shapes.length > MICRO_LIMITS.maxShapes) return 'tooDetailed';
    let points = 0;
    for (const shape of mark.shapes) {
      points += shape.points.length;
      if (shape.kind === 'polyline' && (shape.points.length < 2 || shape.points.length > MICRO_LIMITS.maxPolylinePoints)) {
        return 'tooDetailed';
      }
      if ((shape.kind === 'line' || shape.kind === 'polyline') && pathLengthPx(shape.points) === 0) return 'noLength';
    }
    if (points > MICRO_LIMITS.maxPoints) return 'tooDetailed';
  }
  return null;
}
